import React, { useEffect, useReducer, useState, useMemo } from "react"
import { connect, useSelector, useDispatch } from "react-redux"
import { useLocation } from "react-router"
import { find, get, includes, isEmpty, reject, map, filter } from "lodash"

import { SymbolDetailModal } from "components/organisms/modals/symbolDetail"
import { Detail } from "components/organisms/modals/symbolDetail/detail"
import { Info } from "components/organisms/modals/symbolDetail/info"
import { Data } from "components/organisms/modals/symbolDetail/data"
import { Performance } from "components/organisms/modals/symbolDetail/performance"
import { Trust } from "components/organisms/modals/symbolDetail/trust"
import { Transfer } from "components/organisms/modals/symbolDetail/transfer"
import { Benefit } from "components/organisms/modals/symbolDetail/benefit"
import { Relation } from "components/organisms/modals/symbolDetail/relataion"
import { Chart } from "components/organisms/modals/symbolDetail/chart"
import { BoardOrder } from "components/organisms/modals/symbolDetail/orderBook"
import { News } from "components/organisms/modals/symbolDetail/news"

import { ReduxSymbolDetailStateType } from "redux/ducks/SymbolDetail"
import {
  TabListType,
  symbolDetailManageReducer,
  initialSymbolDetail,
  useGetSymbolRegulation,
} from "components/actions/symbolDetail"
import { APIResponse } from "redux/apis/types"
import { useHistory } from "react-router"
import { SECURITY_TYPE } from "config/const"
import { checkIsSp, checkParentWindow, setThemeFunction } from "utils/util"
import { UserStorage } from "utils/userStorage"
import { easyDecrypt } from "utils/encrypt"
import { parseQueryString } from "utils/util"
import { showSymbol } from "redux/ducks/SymbolDetail"
import uuid from "uuid/v4"
import store from "redux/store"
import { pageLoadRegist } from "redux/ducks/PageLoad"
import SessionTimeOut from "components/pages/main/sessionTimeOut"
import { CoverLoading } from "components/atoms/utils"
import { chartColorSetting } from "redux/ducks/Chart"
import chartColors from "components/atoms/chart/logic/chartColor"

type SymbolDetail = {
  symbolDetail?: ReduxSymbolDetailStateType
  windowMode?: boolean
}

const TAB_LIST = (symbolDetail: object): TabListType => {
  return [
    {
      id: 0,
      name: "銘柄サマリ",
      component: Detail,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 1,
      name: "企業情報",
      component: Info,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 2,
      name: "時系列データ",
      component: Data,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 3,
      name: "業績",
      component: Performance,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 4,
      name: "信用・証金",
      component: Trust,
      domesticOnly: true,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 5,
      name: "銘柄異動",
      component: Transfer,
      domesticOnly: true,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 6,
      name: "株主優待",
      component: Benefit,
      domesticOnly: true,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 7,
      name: "関連銘柄",
      component: Relation,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 8,
      name: "チャート",
      component: Chart,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 9,
      name: "板情報",
      component: BoardOrder,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
    {
      id: 10,
      name: "ニュース",
      component: News,
      props: {
        symbolDetail: get(symbolDetail, "data"),
      },
    },
  ]
}

const WEB_TAB = [0, 1, 2, 3, 4, 5, 6, 7]
const SP_TAB = [0, 1, 2, 3, 4, 5, 6, 8, 9, 10]

// 0は初回レンダリングを意味し、銘柄サマリと、信用・証金のタブのsymbolDetailのデータは、propsを使用する。
// 1はデータを使用した後を意味し、銘柄サマリと、信用・証金のタブはタブ切り替え時にsymbolDetailのAPIを呼ぶようになる。
export const SymbolDetailContext = React.createContext<{
  flag?: 0 | 1
  setFlag?: Function
  currentTab?: number
  showPerformanceNewsFlag?: boolean
  setShowPerformanceNewsFlag?: Function
}>({})

export const SymbolDetail: React.FC<SymbolDetail> = props => {
  const location = useLocation()
  const param = useMemo(() => {
    if (!location.search || !props.windowMode) return
    return parseQueryString(`?${easyDecrypt(location.search.slice(1))}`)
  }, [location.search])
  const dispatch = useDispatch()

  const symbolDetailSelector = (state: any) => state.symbolDetail
  const symbolDetail = useSelector(symbolDetailSelector)
  const [symbolDetailState, symbolDetailDispatcher] = useReducer(
    symbolDetailManageReducer,
    initialSymbolDetail
  )
  //スマホで業績のニュースをモーダルで開くと閉じるボタンが効かなくなるので一時的に銘柄検索詳細のモーダルを消すフラグ
  const [showPerformanceNewsFlag, setShowPerformanceNewsFlag] = useState(false)
  const [symbolDetailFlag, setSymbolDetailFlag] = useState<0 | 1>(0)
  const history = useHistory()
  const loadingId = useMemo(() => uuid(), [])
  const isSp = useMemo(() => {
    return checkIsSp()
  }, [])

  useEffect(() => {
    checkParentWindow()
  }, [])

  useEffect(() => {
    setThemeFunction()
    const elements = document.querySelector("html").className.split(" ")
    const ChartColors = includes(elements, "dark")
      ? UserStorage.TechChartColor.getDark()
      : UserStorage.TechChartColor.getWhite()
    dispatch(
      !isEmpty(ChartColors)
        ? chartColorSetting(ChartColors)
        : chartColorSetting(chartColors.initialize())
    )
  }, [])

  //redux側でタブ変更が起きた場合に変更されてる
  useEffect(() => {
    if (symbolDetailState.currentTab !== get(symbolDetail, "tab")) {
      symbolDetailDispatcher({
        type: "SYMBOL_DETAIL_CHANGE",
        tab: get(symbolDetail, "tab"),
      })
    }
  }, [get(symbolDetail, "tab")])

  useEffect(() => {
    // モーダルが開く時の、タブが銘柄サマリと、信用・証金のタブ以外は、受け取り済みフラグに変える。
    if (
      !includes([0, 4], symbolDetailState.currentTab) &&
      get(symbolDetail, "show")
    )
      setSymbolDetailFlag(1)
  }, [get(symbolDetail, "show")])

  useEffect(() => {
    if (get(symbolDetail, "loading")) {
      store.dispatch(pageLoadRegist(loadingId, true))
    } else {
      store.dispatch(pageLoadRegist(loadingId, false))
    }
  }, [get(symbolDetail, "loading")])

  // sso元のparamを監視。
  useEffect(() => {
    if (!props.windowMode) return
    const cheakFalse = (target: number) => {
      return !target && target !== 0
    }
    const SecurityType = Number(get(param, "SecurityType"))
    const Exchange = Number(get(param, "Exchange"))
    const Symbol = get(param, "Symbol")
    const tab = Number(get(param, "tab")) || get(symbolDetail, "tab", 0)
    if (cheakFalse(SecurityType) || cheakFalse(Exchange) || !Symbol) return
    ;(async () => {
      await dispatch(showSymbol({ SecurityType, Exchange, Symbol }))
      symbolDetailDispatcher({
        type: "SYMBOL_DETAIL_CHANGE",
        tab: tab,
      })
    })()
  }, [param])

  const regulation: Array<APIResponse.Res> = useGetSymbolRegulation(
    {
      Symbol: String(get(symbolDetail, "data.Symbol.Symbol", "")),
      SecurityType: get(symbolDetail, "data.Symbol.SecurityType", ""),
      Exchange: get(symbolDetail, "data.Symbol.Exchange", ""),
    },
    String(get(symbolDetail, "data.Symbol.Symbol", "")),
    get(symbolDetail, "callRegulation")
  )

  // ベースになるtablist作成。
  // web or sp || 国内株 or 外国株 でフィルタリング。
  const tabList = useMemo(() => {
    const _tab = filter(TAB_LIST(symbolDetail), (_, id) =>
      includes(!isSp ? WEB_TAB : SP_TAB, id)
    )
    return get(symbolDetail, ["data", "Symbol", "SecurityType"]) ===
      SECURITY_TYPE.Equity.id
      ? _tab
      : reject(_tab, { domesticOnly: true })
  }, [get(symbolDetail, "data")])

  // 現在選択されているタブのコンポーネントを取得。
  const tabContent = useMemo(() => {
    if (
      !includes(
        map(tabList, items => {
          return items.id
        }),
        symbolDetailState.currentTab
      )
    ) {
      symbolDetailDispatcher({ type: "SYMBOL_DETAIL_CHANGE", tab: 0 })
      return find(tabList, { id: 0 })
    }
    return find(tabList, { id: symbolDetailState.currentTab })
  }, [symbolDetailState.currentTab, tabList])

  // useEffect(() => {
  //   // 外国株の場合、株主優待を表示しない。
  //   // 前回のモーダルが、株主優待で閉じているかつ、外国株の場合、銘柄サマリを開く。
  //   if (isEmpty(get(symbolDetail, "data"))) return
  //   if (
  //     get(tabList[symbolDetailState.currentTab], "domesticOnly") &&
  //     get(get(symbolDetail, "data"), ["Symbol", "SecurityType"]) !==
  //       SECURITY_TYPE.Equity.id
  //   )
  //     symbolDetailDispatcher({ type: "SYMBOL_DETAIL_CHANGE", tab: 0 })
  // }, [get(symbolDetail, "data")])

  return (
    <SymbolDetailContext.Provider
      value={{
        flag: symbolDetailFlag,
        setFlag: setSymbolDetailFlag,
        currentTab: symbolDetailState.currentTab,
        showPerformanceNewsFlag: showPerformanceNewsFlag,
        setShowPerformanceNewsFlag: setShowPerformanceNewsFlag,
      }}
    >
      <CoverLoading targetCls="panel-body" />
      <SymbolDetailModal
        show={get(symbolDetail, "show")}
        components={tabContent}
        onHide={() => {
          symbolDetailDispatcher({ type: "SYMBOL_DETAIL_HIDE" })
          // このコンポーネントはアンマウントされないため、モーダルを消す関数内にflagをリセットする処理を入れている。
          setSymbolDetailFlag(0)
        }}
        tabList={tabList}
        symbolDetail={get(symbolDetail, "data")}
        symbolDetailState={symbolDetailState}
        changeTab={(tab: number) =>
          symbolDetailDispatcher({ type: "SYMBOL_DETAIL_CHANGE", tab: tab })
        }
        regulation={regulation}
        history={history}
        windowMode={!!props.windowMode}
      />
      <SessionTimeOut />
    </SymbolDetailContext.Provider>
  )
}
